import React, { useState } from 'react';
import { AlertTriangle, AlertCircle, ArrowRight, X } from 'lucide-react';
import { ConsistencyFinding } from '../../types';

/** Map a finding field path back to the wizard step that owns it. */
function stepForPath(path: string): string | null {
    if (path.startsWith('deceased.') || path.startsWith('family.')) return 'basics';
    if (path.startsWith('assets.properties')) return 'property';
    if (path.startsWith('assets.bank_accounts')) return 'banks';
    if (path.startsWith('assets.investments')) return 'investments';
    if (path.startsWith('assets.lifetime_gifts')) return 'gifts';
    if (path.startsWith('iht_details')) return 'beneficiaries';
    if (path === 'assets' || path.startsWith('assets')) return 'discovery';
    return null;
}

/**
 * Collapse findings that name the same set of fields into one entry, so a single
 * contradiction the pass emits more than once isn't stacked (§4). Findings whose
 * field-path sets differ are kept separate.
 */
function groupFindings(findings: ConsistencyFinding[]): ConsistencyFinding[] {
    const seen = new Map<string, ConsistencyFinding>();
    for (const f of findings) {
        const key = f.fields.map((x) => x.path).sort().join('|') || f.message;
        if (!seen.has(key)) seen.set(key, f);
    }
    return [...seen.values()];
}

/** A finding's message plus one jump-link per field named in its references. */
const FindingBody: React.FC<{
    finding: ConsistencyFinding;
    tone: 'blocking' | 'review';
    goToStep?: (stepId: string) => boolean;
}> = ({ finding, tone, goToStep }) => (
    <div className="flex flex-col gap-2">
        <p className={`text-[13px] leading-relaxed m-0 ${tone === 'blocking' ? 'text-[#1A1A1A]' : 'text-[#3F3D39]'}`}>
            {finding.message}
        </p>
        <div className="flex flex-wrap gap-2 no-print">
            {finding.fields.map((field, i) => {
                const step = stepForPath(field.path);
                if (!step || !goToStep) {
                    return <span key={i} className="text-[11px] text-[#6B6762] italic">{field.label}</span>;
                }
                return (
                    <button
                        key={i}
                        type="button"
                        onClick={() => goToStep(step)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#3730A3] hover:text-[#2E2780] hover:underline bg-[#EEF0FA] px-2 py-1 rounded border border-[#C7CBEA] transition-colors"
                    >
                        {field.label} <ArrowRight className="w-3 h-3" />
                    </button>
                );
            })}
        </div>
    </div>
);

/**
 * Blocking conflict panel — genuine contradictions (H1–H3). Rendered prominently
 * at the top of the report; the confident headline is gated separately by the
 * caller. Each finding names both conflicting facts and links to both fields so
 * the user can go straight to either side. The panel never picks a side.
 */
export const ConsistencyConflictPanel: React.FC<{
    findings: ConsistencyFinding[];
    goToStep?: (stepId: string) => boolean;
}> = ({ findings, goToStep }) => {
    const grouped = groupFindings(findings);
    if (grouped.length === 0) return null;

    return (
        <div data-testid="consistency-conflict-panel" className="bg-white border border-[#1A1A1A] rounded overflow-hidden">
            <div className="px-5 py-4 border-b border-[#1A1A1A] flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#1A1A1A] flex-shrink-0 mt-0.5" />
                <div>
                    <h3 className="font-serif text-[18px] font-medium text-[#1A1A1A] m-0">
                        {grouped.length === 1 ? 'Some answers conflict' : `${grouped.length} answers conflict`}
                    </h3>
                    <p className="text-[12px] text-[#3F3D39] mt-1 m-0">
                        Resolve {grouped.length === 1 ? 'this' : 'these'} before relying on the result — correct one side of each. The tool won't choose for you.
                    </p>
                </div>
            </div>
            <ul className="px-5 py-2 divide-y divide-[#E8E4DB] list-none m-0">
                {grouped.map((f, i) => (
                    <li key={`${f.code}-${i}`} className="py-3 first:pt-0 last:pb-0">
                        <FindingBody finding={f} tone="blocking" goToStep={goToStep} />
                    </li>
                ))}
            </ul>
        </div>
    );
};

/**
 * Review nudges — unusual-but-possible items (S1–S4). Soft, skippable, and
 * dismissible; they never gate the result. Rendered inline near the report body,
 * not at the top.
 */
export const ConsistencyReviewNudges: React.FC<{
    findings: ConsistencyFinding[];
    goToStep?: (stepId: string) => boolean;
}> = ({ findings, goToStep }) => {
    const grouped = groupFindings(findings);
    const [dismissed, setDismissed] = useState<Set<string>>(new Set());
    const visible = grouped.filter((f, i) => !dismissed.has(`${f.code}-${i}`));
    if (visible.length === 0) return null;

    return (
        <div data-testid="consistency-review-nudges" className="bg-[#FAF3E0] border border-[#E8D89A] rounded overflow-hidden no-print">
            <div className="px-5 py-3 border-b border-[#E8D89A] flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#7A5A00] flex-shrink-0" />
                <h3 className="text-xs font-bold text-[#5A4200] uppercase tracking-wide m-0">
                    A couple of things look unusual — worth a check
                </h3>
            </div>
            <ul className="px-5 py-2 divide-y divide-[#E8D89A] list-none m-0">
                {grouped.map((f, i) => {
                    const id = `${f.code}-${i}`;
                    if (dismissed.has(id)) return null;
                    return (
                        <li key={id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                            <div className="flex-1">
                                <FindingBody finding={f} tone="review" goToStep={goToStep} />
                            </div>
                            <button
                                type="button"
                                aria-label="Dismiss"
                                onClick={() => setDismissed((prev) => new Set(prev).add(id))}
                                className="flex-shrink-0 text-[#7A5A00] hover:text-[#5A4200] mt-0.5"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
